import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutGrid, History, Bell, User, Settings, LogOut } from "lucide-react";
import { useAuth } from "../auth";
import { usePricing } from "../usePricing";
import { ThemeToggle } from "../theme";
import { PfuLogo } from "./Logo";

const TABS = [
  { to: "/dashboard", label: "Profiles", icon: LayoutGrid },
  { to: "/history", label: "History", icon: History },
  { to: "/alerts", label: "Alerts", icon: Bell },
  { to: "/account", label: "Account", icon: User },
];

const ADMIN_TAB = { to: "/admin", label: "Admin", icon: Settings };

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const pricing = usePricing();
  const navigate = useNavigate();
  const tabs = user?.is_admin ? [...TABS, ADMIN_TAB] : TABS;

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-full">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col fixed inset-y-0 left-0 w-64 bg-stone-surface border-r border-stone-border p-5">
        <button
          onClick={() => navigate("/dashboard")}
          className="flex items-center gap-2.5 mb-8 text-left"
          data-testid="brand-logo"
        >
          <PfuLogo size={36} />
          <div>
            <div className="font-display font-bold text-ink leading-none">PFU Tracker</div>
            <div className="text-[11px] text-muted mt-1">Mauritius price watch</div>
          </div>
        </button>
        <nav className="flex flex-col gap-1">
          {tabs.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              data-testid={`nav-${t.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 min-h-[44px] rounded-card text-[15px] transition-colors ${
                  isActive ? "bg-teal-light text-teal font-medium" : "text-ink hover:bg-stone-border/40"
                }`
              }
            >
              <t.icon className="w-5 h-5" />
              {t.label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto pt-5 border-t border-stone-border">
          <div className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-ink truncate">{user?.name}</div>
              <div className="text-xs text-muted truncate">{user?.email}</div>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              title="Log out"
              className="w-9 h-9 rounded-card border border-stone-border flex items-center justify-center text-muted hover:text-coral hover:border-coral transition-colors shrink-0"
            >
              <LogOut className="w-4 h-4" />
            </button>
            <ThemeToggle className="shrink-0" />
          </div>
          <div className="chip bg-teal-light text-teal mt-2">Pro · {pricing.display}/{pricing.period}</div>
        </div>
      </aside>

      {/* Main */}
      <main className="md:pl-64 pb-24 md:pb-8">
        {/* Mobile top bar */}
        <div className="md:hidden sticky top-0 z-30 flex items-center justify-between px-4 h-14 bg-stone-surface/90 backdrop-blur-md border-b border-stone-border">
          <div className="flex items-center gap-2">
            <PfuLogo size={28} />
            <span className="font-display font-bold text-ink text-sm">PFU Tracker</span>
          </div>
          <ThemeToggle />
        </div>
        <div className="max-w-3xl mx-auto px-4 md:px-8 pt-5 md:pt-8">{children}</div>
      </main>

      {/* Mobile bottom tabs */}
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 bg-stone-surface border-t border-stone-border flex z-40"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        {tabs.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            data-testid={`tab-${t.label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center justify-center gap-1 min-h-[60px] text-[11px] ${
                isActive ? "text-teal" : "text-muted"
              }`
            }
          >
            <t.icon className="w-5 h-5" />
            {t.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
